/**
 * AURA Panel Component
 * Main sliding panel with tabs for different adjustment categories
 */

import { AuraConfig } from '../core/types';
import { stateManager } from '../core/state';
import { t } from '../core/i18n';
import { getIcon } from './styles/icons';
import { LOGO_ICON_TRANSPARENT } from './styles/logo-data';
import { PANEL_STYLES } from './styles/panel-styles';
import { createLanguageSelector } from './language-selector';
import { createToolbar } from './toolbar';
import { createSearchBar } from './search-bar';
import { createStatementModal, StatementModal } from './statement-modal';

export type TabId = 'profiles' | 'content' | 'colors' | 'orientation';

export interface PanelElements {
  panel: HTMLElement;
  overlay: HTMLElement;
  contentContainer: HTMLElement;
  tabPanels: Map<TabId, HTMLElement>;
  statementModal: StatementModal;
}

export function createPanel(config: AuraConfig): { elements: PanelElements; styles: string } {
  const isLeft = config.position?.includes('left');

  const overlay = document.createElement('div');
  overlay.className = 'aura-overlay';
  overlay.setAttribute('aria-hidden', 'true');
  overlay.addEventListener('click', () => stateManager.setPanelOpen(false));

  const panel = document.createElement('div');
  panel.className = `aura-panel${isLeft ? ' aura-panel--left' : ''}`;
  panel.setAttribute('role', 'dialog');
  panel.setAttribute('aria-modal', 'true');
  panel.setAttribute('aria-label', t('panelTitle'));
  panel.setAttribute('data-aura', 'panel');

  // Header
  const header = buildHeader(config);

  // Statement modal
  const statementModal = createStatementModal(config);

  // Toolbar (Reset Settings, Statement, Hide Interface)
  const toolbar = createToolbar({
    onStatement: () => statementModal.open(),
    onHideInterface: () => {
      stateManager.setPanelOpen(false);
      stateManager.setHidden(true);
    },
  });

  // Search bar
  const searchBarResult = createSearchBar();

  // Tabs
  const { tabsEl, tabPanels } = buildTabs();

  // Content container
  const contentContainer = document.createElement('div');
  contentContainer.className = 'aura-panel__content';

  tabPanels.forEach((tabPanel) => {
    contentContainer.appendChild(tabPanel);
  });

  // Footer
  const footer = buildFooter();

  // Assemble panel
  panel.appendChild(header);
  panel.appendChild(toolbar);
  panel.appendChild(searchBarResult.element);
  panel.appendChild(tabsEl);
  panel.appendChild(contentContainer);
  panel.appendChild(footer);

  // Subscribe to state changes
  stateManager.subscribe((state) => {
    panel.classList.toggle('aura-panel--open', state.isOpen);
    overlay.classList.toggle('aura-overlay--visible', state.isOpen);

    if (state.isOpen) {
      const closeBtn = header.querySelector<HTMLButtonElement>('.aura-panel__btn');
      closeBtn?.focus();
    }
  });

  panel.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      stateManager.setPanelOpen(false);
    }
  });

  return {
    elements: { panel, overlay, contentContainer, tabPanels, statementModal },
    styles: PANEL_STYLES,
  };
}

function buildHeader(_config: AuraConfig): HTMLElement {
  const header = document.createElement('div');
  header.className = 'aura-panel__header';

  const brand = document.createElement('div');
  brand.className = 'aura-panel__brand';

  const logo = document.createElement('img');
  logo.className = 'aura-panel__logo';
  logo.src = LOGO_ICON_TRANSPARENT;
  logo.alt = 'AURA';

  const title = document.createElement('h2');
  title.className = 'aura-panel__title';
  title.textContent = 'AURA Accessibility';

  brand.appendChild(logo);
  brand.appendChild(title);

  const headerRight = document.createElement('div');
  headerRight.className = 'aura-panel__header-right';

  const langSelector = createLanguageSelector();
  headerRight.appendChild(langSelector);

  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'aura-panel__btn';
  closeBtn.setAttribute('aria-label', t('close'));
  closeBtn.innerHTML = getIcon('close');
  closeBtn.addEventListener('click', () => stateManager.setPanelOpen(false));
  headerRight.appendChild(closeBtn);

  header.appendChild(brand);
  header.appendChild(headerRight);

  return header;
}

interface TabsResult {
  tabsEl: HTMLElement;
  tabPanels: Map<TabId, HTMLElement>;
  setActiveTab: (id: TabId) => void;
}

function buildTabs(): TabsResult {
  const tabs = document.createElement('div');
  tabs.className = 'aura-panel__tabs';
  tabs.setAttribute('role', 'tablist');

  const tabData: { id: TabId; labelKey: string }[] = [
    { id: 'profiles', labelKey: 'tabProfiles' },
    { id: 'content', labelKey: 'tabContent' },
    { id: 'colors', labelKey: 'tabColors' },
    { id: 'orientation', labelKey: 'tabOrientation' },
  ];

  const tabPanels = new Map<TabId, HTMLElement>();
  let activeTab: TabId = 'profiles';

  const setActiveTab = (id: TabId) => {
    tabs.querySelectorAll('.aura-panel__tab').forEach((t) => {
      t.classList.remove('aura-panel__tab--active');
      t.setAttribute('aria-selected', 'false');
    });
    const targetTab = tabs.querySelector(`#aura-tab-${id}`);
    targetTab?.classList.add('aura-panel__tab--active');
    targetTab?.setAttribute('aria-selected', 'true');
    tabPanels.forEach((panel, panelId) => {
      panel.classList.toggle('aura-tab-panel--active', panelId === id);
    });
    activeTab = id;
  };

  tabData.forEach(({ id, labelKey }) => {
    const tab = document.createElement('button');
    tab.type = 'button';
    tab.className = `aura-panel__tab${id === activeTab ? ' aura-panel__tab--active' : ''}`;
    tab.setAttribute('role', 'tab');
    tab.setAttribute('aria-selected', String(id === activeTab));
    tab.setAttribute('aria-controls', `aura-tabpanel-${id}`);
    tab.id = `aura-tab-${id}`;
    tab.textContent = t(labelKey as any);

    tab.addEventListener('click', () => setActiveTab(id));
    tabs.appendChild(tab);
  });

  tabData.forEach(({ id }) => {
    const tabPanel = document.createElement('div');
    tabPanel.className = `aura-tab-panel${id === activeTab ? ' aura-tab-panel--active' : ''}`;
    tabPanel.id = `aura-tabpanel-${id}`;
    tabPanel.setAttribute('role', 'tabpanel');
    tabPanel.setAttribute('aria-labelledby', `aura-tab-${id}`);
    tabPanels.set(id, tabPanel);
  });

  return { tabsEl: tabs, tabPanels, setActiveTab };
}

function buildFooter(): HTMLElement {
  const footer = document.createElement('div');
  footer.className = 'aura-panel__footer';

  const footerLink = document.createElement('a');
  footerLink.className = 'aura-panel__footer-link';
  footerLink.href = '#';
  footerLink.textContent = t('accessibilityStatement');
  footerLink.addEventListener('click', (e) => {
    e.preventDefault();
  });

  const footerBrand = document.createElement('div');
  footerBrand.className = 'aura-panel__footer-brand';

  const brandLogo = document.createElement('img');
  brandLogo.src = LOGO_ICON_TRANSPARENT;
  brandLogo.alt = '';
  brandLogo.width = 14;
  brandLogo.height = 14;

  const brandText = document.createElement('span');
  brandText.textContent = t('poweredBy');

  footerBrand.appendChild(brandLogo);
  footerBrand.appendChild(brandText);

  footer.appendChild(footerLink);
  footer.appendChild(footerBrand);

  return footer;
}

